/**
 * Analytics.
 *
 * Built against taskdojo-app/src/screens/AnalyticsScreen.js. Every figure --
 * completion rate, consistency, perfect days, the productivity weighting --
 * is that screen's calculation, so the two platforms report the same numbers.
 *
 * The charts are not a transliteration, because the phone's and a browser's
 * affordances differ, but they follow one rule that is worth writing down:
 * colour is assigned by the job it does, not to decorate.
 *
 *   - completion trend, busiest days, category counts, per-habit rates are all
 *     *magnitude*, single series. They get one hue. Giving each bar its own
 *     colour would imply an identity the data does not have.
 *   - task status is *state*, so it uses the reserved status colours -- but as
 *     labelled rows, never a colour-only stacked bar. Checked rather than
 *     assumed: #E34234 and #4C9A2A are ΔE 1.6 apart under deuteranopia, which
 *     is indistinguishable. The label carries the identity; the swatch only
 *     reinforces it.
 *   - moods and habits own their colour, so theirs is used -- colour following
 *     the entity, which is the one case where it is meaningful.
 *
 * The magnitude hue is #5B8FD6 rather than the app's #4A6FA5. That was not a
 * preference: #4A6FA5 measures 2.89:1 against the dark card, under the 3:1
 * floor for a mark. #5B8FD6 is the same blue lifted until it passes, on both
 * the dark and the light surface.
 *
 * Not ported: the AI summary. It is a separate Cloud Function with its own
 * caching and Pro checks, and is better added deliberately than bolted on.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useApp } from '../../contexts/AppContext';
import { useTheme } from '../../contexts/ThemeContext';
import { ScreenHeader, FilterPill, SectionTitle } from '../dojo';
import { MOOD_OPTIONS, Task, Habit } from '../../services/firebaseService';
import { dayKey } from '../../utils/score';
import { readableOn, MARK_MIN } from '../../utils/taskColor';

/** Validated against both surfaces; see the note above. */
const MAGNITUDE = '#5B8FD6';

const STATUS_COLORS: Record<string, string> = {
  not_started: '#8E8E93',
  in_progress: '#4A6FA5',
  paused: '#C1763C',
  completed: '#4C9A2A',
};
const STATUS_LABELS: Record<string, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  paused: 'Paused',
  completed: 'Completed',
};

const PERIODS = [
  { key: '7d', label: '7 days', days: 7 },
  { key: '30d', label: '30 days', days: 30 },
  { key: '90d', label: '90 days', days: 90 },
];
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const asDate = (v: any): Date | null => {
  if (!v) return null;
  const d = typeof v?.toDate === 'function' ? v.toDate() : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/* ------------------------------------------------------------------ *
 * One bar chart, one series.
 *
 * Thin marks with rounded data-ends anchored to the baseline, a 2px gap
 * between bars, a recessive baseline, and a hover tooltip on every bar --
 * an HTML chart is interactive, so it ships with the hover layer rather
 * than making people guess a value from a height.
 * ------------------------------------------------------------------ */
const BarChart: React.FC<{
  data: Array<{ label: string; value: number; title?: string }>;
  height?: number;
  color?: string;
  /** Show every label, or thin them out when there are many bars. */
  labelEvery?: number;
}> = ({ data, height = 96, color = MAGNITUDE, labelEvery = 1 }) => {
  const { colors, isDark } = useTheme();
  /** The surface marks and chips sit on, which is what contrast is measured against. */
  const card = isDark ? '#1B2A32' : '#FFFFFF';

  const max = Math.max(...data.map((d) => d.value), 1);
  const track = isDark ? 'rgba(148,163,184,0.10)' : 'rgba(120,113,108,0.10)';

  return (
    <div>
      <div className="flex items-end gap-[2px]" style={{ height }} role="img"
        aria-label={data.map((d) => `${d.label}: ${d.value}`).join(', ')}>
        {data.map((d, i) => (
          <div key={i} className="flex-1 h-full flex items-end relative group" style={{ minWidth: 3 }}>
            {/* The empty part of the column, so a zero still reads as a slot. */}
            <div className="absolute inset-0 rounded-t-[4px]" style={{ backgroundColor: track }} />
            <div
              className="relative w-full rounded-t-[4px] transition-[height] duration-300"
              style={{
                height: `${Math.max((d.value / max) * 100, d.value > 0 ? 4 : 0)}%`,
                backgroundColor: color,
              }}
            />
            <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5
              opacity-0 group-hover:opacity-100 transition-opacity z-20 whitespace-nowrap">
              <span className={`block rounded-[8px] border-2 ${colors.cardBorder} ${colors.card}
                px-2 py-1 text-[11.5px] font-bold shadow-lg`}>
                {d.title || d.label}: {d.value}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-[2px] mt-1.5">
        {data.map((d, i) => (
          <span key={i}
            className={`flex-1 text-center text-[10px] font-bold tabular-nums truncate ${colors.textMuted}`}
            style={{ minWidth: 3 }}>
            {i % labelEvery === 0 ? d.label : ''}
          </span>
        ))}
      </div>
    </div>
  );
};

/** A labelled horizontal bar: name, mark, value. Identity from the label. */
const Row: React.FC<{ label: string; value: number; max: number; color?: string; suffix?: string }> =
({ label, value, max, color = MAGNITUDE, suffix }) => {
  const { colors, isDark } = useTheme();
  return (
    <div className="flex items-center gap-3">
      <span className={`w-[92px] flex-shrink-0 text-[12.5px] font-bold truncate ${colors.textSecondary}`}>
        {label}
      </span>
      <span className={`flex-1 h-2.5 rounded-full overflow-hidden
        ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-200'}`}>
        <span className="block h-full rounded-full transition-[width] duration-300"
          style={{ width: `${max > 0 ? (value / max) * 100 : 0}%`, backgroundColor: color }} />
      </span>
      <span className="w-[46px] text-right text-[12.5px] font-extrabold tabular-nums flex-shrink-0">
        {value}{suffix || ''}
      </span>
    </div>
  );
};

const Analytics: React.FC = () => {
  const { state } = useApp();
  const { colors, isDark } = useTheme();

  const uid = state.user?.uid;
  const tasks = (state.tasks || []) as Task[];
  const habits = (state.habits || []) as Habit[];
  const completions = state.completions || {};

  const [period, setPeriod] = useState('7d');
  const [moods, setMoods] = useState<any[]>([]);

  useEffect(() => {
    if (!uid) return;
    const unsub = onSnapshot(collection(db, 'users', uid, 'feelings'),
      (s) => setMoods(s.docs.map((d) => d.data())), () => {});
    return unsub;
  }, [uid]);

  const days = PERIODS.find((p) => p.key === period)?.days || 7;

  const range = useMemo(() => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date();
    start.setDate(start.getDate() - (days - 1));
    start.setHours(0, 0, 0, 0);
    return { start, end };
  }, [days]);

  const dateKeys = useMemo(() => {
    const out: string[] = [];
    const cursor = new Date(range.start);
    while (cursor <= range.end) {
      out.push(dayKey(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return out;
  }, [range]);

  /* ------------------------------- tasks ------------------------------- */
  const taskStats = useMemo(() => {
    const inPeriod = tasks.filter((t) => {
      const c = asDate(t.createdAt);
      return c && c >= range.start && c <= range.end;
    });
    const completed = inPeriod.filter((t) => t.completed);
    const rate = inPeriod.length > 0 ? Math.round((completed.length / inPeriod.length) * 100) : 0;

    const statusCounts: Record<string, number> = {
      not_started: 0, in_progress: 0, paused: 0, completed: 0,
    };
    inPeriod.forEach((t) => {
      const s = t.completed ? 'completed' : (t.status || 'not_started');
      if (statusCounts[s] !== undefined) statusCounts[s] += 1;
    });

    const categoryCounts: Record<string, number> = {};
    inPeriod.forEach((t) => {
      const cat = t.category || 'General';
      categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
    });
    const categoryData = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);

    const dayOfWeek = [0, 0, 0, 0, 0, 0, 0];
    tasks.forEach((t) => {
      if (!t.completed) return;
      const d = asDate(t.completedAt);
      if (d && d >= range.start && d <= range.end) dayOfWeek[d.getDay()] += 1;
    });

    const daily = dateKeys.map((key) => ({
      key,
      value: tasks.filter((t) => {
        if (!t.completed) return false;
        const d = asDate(t.completedAt);
        return d && dayKey(d) === key;
      }).length,
    }));

    const times: number[] = [];
    completed.forEach((t) => {
      const c = asDate(t.createdAt);
      const f = asDate(t.completedAt);
      if (c && f) times.push(Math.max(0, Math.round((f.getTime() - c.getTime()) / 86400000)));
    });
    const avgDays = times.length
      ? (times.reduce((a, b) => a + b, 0) / times.length).toFixed(1)
      : null;

    return {
      created: inPeriod.length,
      completed: completed.length,
      rate,
      statusCounts,
      categoryData,
      dayOfWeek,
      daily,
      avgDays,
    };
  }, [tasks, range, dateKeys]);

  /* ------------------------------- habits ------------------------------- */
  const habitStats = useMemo(() => {
    if (habits.length === 0) return null;

    // Freezes count as maintained, as on the phone -- a frozen day is not a
    // miss, and counting it as one would understate consistency.
    const maintainedOn = (key: string) => {
      const day: any = (completions as any)[key] || {};
      const done: string[] = Array.isArray(day) ? day : (day.habits || []);
      const frozen: string[] = Array.isArray(day) ? [] : (day.freezes || []);
      return new Set([...done, ...frozen]);
    };

    const dailyRates = dateKeys.map((key) => {
      const set = maintainedOn(key);
      return { key, completed: set.size, total: habits.length };
    });

    const daysWithAny = dailyRates.filter((d) => d.completed > 0).length;
    const consistency = Math.round((daysWithAny / dateKeys.length) * 100);
    const perfectDays = dailyRates.filter((d) => d.completed >= habits.length).length;

    const perHabit = habits.map((h) => {
      const kept = dateKeys.filter((key) => maintainedOn(key).has(h.id)).length;
      return { habit: h, kept, rate: Math.round((kept / dateKeys.length) * 100) };
    }).sort((a, b) => b.rate - a.rate);

    return { consistency, perfectDays, dailyRates, perHabit };
  }, [habits, completions, dateKeys]);

  /* -------------------------------- moods -------------------------------- */
  const moodStats = useMemo(() => {
    const inPeriod = moods.filter((m) => {
      const d = asDate(m.timestamp);
      return d && d >= range.start && d <= range.end;
    });
    if (inPeriod.length === 0) return null;

    const counts: Record<string, number> = {};
    inPeriod.forEach((m) => { counts[m.feeling] = (counts[m.feeling] || 0) + 1; });

    const custom = ((state.user as any)?.customMoods || []) as any[];
    const all = [...(MOOD_OPTIONS as any[]), ...custom];

    const distribution = Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([id, count]) => {
      const opt = all.find((o) => o.id === id)
        || { emoji: '😶', label: id, color: '#8E8E93', sentiment: 'neutral' };
      return { ...opt, id, count, percentage: Math.round((count / inPeriod.length) * 100) };
    });

    let positive = 0;
    inPeriod.forEach((m) => {
      if (all.find((o) => o.id === m.feeling)?.sentiment === 'positive') positive += 1;
    });

    return {
      total: inPeriod.length,
      distribution,
      positiveRatio: Math.round((positive / inPeriod.length) * 100),
    };
  }, [moods, range, state.user]);

  /* ----------------------------- the headline ----------------------------- */
  const productivity = useMemo(() => {
    const t = taskStats.rate;
    const h = habitStats?.consistency || 0;
    const m = moodStats?.positiveRatio || 0;

    // The phone's weighting exactly, including how it redistributes when a
    // signal is missing -- otherwise someone with no habits scores lower here
    // than on their phone for the same work.
    let score: number;
    if (habitStats && moodStats) score = Math.round(t * 0.4 + h * 0.3 + m * 0.3);
    else if (habitStats) score = Math.round(t * 0.55 + h * 0.45);
    else if (moodStats) score = Math.round(t * 0.55 + m * 0.45);
    else score = t;

    let label = 'Getting started';
    let emoji = '🌱';
    if (score >= 80) { label = 'Outstanding'; emoji = '🔥'; }
    else if (score >= 60) { label = 'Great work'; emoji = '💪'; }
    else if (score >= 40) { label = 'Building momentum'; emoji = '🚀'; }
    else if (score >= 20) { label = 'Making progress'; emoji = '📈'; }
    return { score, label, emoji };
  }, [taskStats, habitStats, moodStats]);

  const card = `rounded-[18px] border-2 border-b-[3px] ${colors.cardBorder} ${colors.card} p-4`;
  const statusTotal = Object.values(taskStats.statusCounts).reduce((a, b) => a + b, 0);
  const trendLabelEvery = days <= 7 ? 1 : days <= 30 ? 5 : 15;

  return (
    <div className={`min-h-screen ${colors.bg} ${colors.text}`}>
      <div className="max-w-3xl mx-auto pt-4 pb-24">
        <ScreenHeader title="Analytics" subtitle={`Last ${days} days`} />

        {/* Filters in one row above the charts. */}
        <div className="flex gap-2 px-5 pb-4">
          {PERIODS.map((p) => (
            <FilterPill key={p.key} active={period === p.key} onClick={() => setPeriod(p.key)}>
              {p.label}
            </FilterPill>
          ))}
        </div>

        <div className="px-5 space-y-5">
          {/* The headline is a number, not a chart. */}
          <div className={card}>
            <div className="flex items-center gap-4">
              <span className="text-[40px] leading-none">{productivity.emoji}</span>
              <div>
                <p className="text-[40px] font-black tracking-[-1.5px] leading-none tabular-nums">
                  {productivity.score}
                  <span className={`text-[20px] ${colors.textMuted}`}>/100</span>
                </p>
                <p className={`text-[14px] font-extrabold mt-1 ${colors.textSecondary}`}>
                  {productivity.label}
                </p>
              </div>
            </div>
            <p className={`text-[12px] font-semibold mt-3 ${colors.textMuted}`}>
              Weighs task completion, habit consistency and mood — whichever of those you use.
            </p>
          </div>

          {/* -------------------------------- tasks -------------------------------- */}
          <div className={card}>
            <SectionTitle className="mb-4">Tasks</SectionTitle>

            <div className="grid grid-cols-3 gap-3 mb-5">
              {[
                { label: 'Created', value: taskStats.created },
                { label: 'Completed', value: taskStats.completed, color: '#4C9A2A' },
                { label: 'Rate', value: `${taskStats.rate}%`, color: MAGNITUDE },
              ].map((s) => (
                <div key={s.label} className={`rounded-[12px] border ${colors.border}
                  ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-50'} px-3 py-2.5 text-center`}>
                  <p className="text-[20px] font-black tracking-[-0.5px] tabular-nums"
                    style={s.color ? { color: s.color } : undefined}>{s.value}</p>
                  <p className={`text-[11px] font-bold uppercase tracking-[0.4px] ${colors.textSecondary}`}>
                    {s.label}
                  </p>
                </div>
              ))}
            </div>

            {taskStats.avgDays && (
              <p className={`text-[12.5px] font-semibold mb-5 ${colors.textSecondary}`}>
                Average time from created to done: <strong>{taskStats.avgDays} days</strong>
              </p>
            )}

            {/* State, as labelled rows. Never colour alone -- see the file note. */}
            {statusTotal > 0 && (
              <div className="mb-5">
                <p className={`text-[11px] font-bold uppercase tracking-[0.6px] mb-2.5 ${colors.textMuted}`}>
                  Status
                </p>
                <div className="space-y-2">
                  {Object.entries(taskStats.statusCounts).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2.5">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: readableOn(STATUS_COLORS[k], card, MARK_MIN) }} />
                      <span className={`flex-1 text-[13px] font-bold ${colors.textSecondary}`}>
                        {STATUS_LABELS[k]}
                      </span>
                      <span className="text-[13px] font-extrabold tabular-nums">{v}</span>
                      <span className={`w-[42px] text-right text-[12px] font-semibold tabular-nums
                        ${colors.textMuted}`}>
                        {statusTotal > 0 ? Math.round((v / statusTotal) * 100) : 0}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-5">
              <p className={`text-[11px] font-bold uppercase tracking-[0.6px] mb-2.5 ${colors.textMuted}`}>
                Completed per day
              </p>
              <BarChart
                data={taskStats.daily.map((d) => ({
                  label: d.key.slice(8),
                  value: d.value,
                  title: d.key,
                }))}
                labelEvery={trendLabelEvery}
              />
            </div>

            <div className="mb-5">
              <p className={`text-[11px] font-bold uppercase tracking-[0.6px] mb-2.5 ${colors.textMuted}`}>
                Busiest days
              </p>
              <BarChart
                height={72}
                data={taskStats.dayOfWeek.map((v, i) => ({ label: DAY_LABELS[i], value: v }))}
              />
            </div>

            {taskStats.categoryData.length > 0 && (
              <div>
                <p className={`text-[11px] font-bold uppercase tracking-[0.6px] mb-2.5 ${colors.textMuted}`}>
                  By category
                </p>
                <div className="space-y-2">
                  {taskStats.categoryData.map(([name, count]) => (
                    <Row key={name} label={name} value={count}
                      max={Math.max(...taskStats.categoryData.map(([, v]) => v), 1)} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ------------------------------- habits ------------------------------- */}
          {habitStats ? (
            <div className={card}>
              <SectionTitle className="mb-4">Habits</SectionTitle>

              <div className="grid grid-cols-2 gap-3 mb-5">
                {[
                  { label: 'Consistency', value: `${habitStats.consistency}%`, color: MAGNITUDE },
                  { label: 'Perfect days', value: habitStats.perfectDays, color: '#4C9A2A' },
                ].map((s) => (
                  <div key={s.label} className={`rounded-[12px] border ${colors.border}
                    ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-50'} px-3 py-2.5 text-center`}>
                    <p className="text-[20px] font-black tracking-[-0.5px] tabular-nums"
                      style={{ color: s.color }}>{s.value}</p>
                    <p className={`text-[11px] font-bold uppercase tracking-[0.4px] ${colors.textSecondary}`}>
                      {s.label}
                    </p>
                  </div>
                ))}
              </div>

              <p className={`text-[11px] font-bold uppercase tracking-[0.6px] mb-2.5 ${colors.textMuted}`}>
                Kept, per habit
              </p>
              <div className="space-y-2">
                {habitStats.perHabit.map(({ habit, rate }) => (
                  // The habit's own colour: identity the data actually has.
                  <Row key={habit.id} label={`${habit.icon || '🎯'} ${habit.title}`}
                    value={rate} max={100} suffix="%"
                    color={readableOn(habit.color || MAGNITUDE, card, MARK_MIN)} />
                ))}
              </div>
            </div>
          ) : (
            <div className={card}>
              <SectionTitle className="mb-2">Habits</SectionTitle>
              <p className={`text-[14px] font-semibold ${colors.textSecondary}`}>
                Nothing to measure yet — add a habit and its consistency shows up here.
              </p>
            </div>
          )}

          {/* -------------------------------- mood -------------------------------- */}
          {moodStats ? (
            <div className={card}>
              <SectionTitle className="mb-4">Feelings</SectionTitle>

              <div className="grid grid-cols-2 gap-3 mb-5">
                {[
                  { label: 'Entries', value: moodStats.total },
                  { label: 'Positive', value: `${moodStats.positiveRatio}%`, color: '#4C9A2A' },
                ].map((s) => (
                  <div key={s.label} className={`rounded-[12px] border ${colors.border}
                    ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-50'} px-3 py-2.5 text-center`}>
                    <p className="text-[20px] font-black tracking-[-0.5px] tabular-nums"
                      style={s.color ? { color: s.color } : undefined}>{s.value}</p>
                    <p className={`text-[11px] font-bold uppercase tracking-[0.4px] ${colors.textSecondary}`}>
                      {s.label}
                    </p>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                {moodStats.distribution.map((m: any) => (
                  <Row key={m.id} label={`${m.emoji} ${m.label}`} value={m.count}
                    max={Math.max(...moodStats.distribution.map((x: any) => x.count), 1)}
                    color={readableOn(m.color, card, MARK_MIN)} />
                ))}
              </div>
            </div>
          ) : (
            <div className={card}>
              <SectionTitle className="mb-2">Feelings</SectionTitle>
              <p className={`text-[14px] font-semibold ${colors.textSecondary}`}>
                Nothing logged in this period.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
